import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  // FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  Chip,
  Divider,
  Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../../services/api';

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  
  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  
  // Form states
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });

  // All available permissions
  const availablePermissions = [
    // Project permissions
    { value: 'projects:read_all', label: 'Lihat Semua Proyek', category: 'Proyek' },
    { value: 'projects:create', label: 'Buat Proyek', category: 'Proyek' },
    { value: 'projects:update', label: 'Update Proyek', category: 'Proyek' },
    { value: 'projects:delete', label: 'Hapus Proyek', category: 'Proyek' },
    { value: 'projects:manage_members', label: 'Kelola Anggota Proyek', category: 'Proyek' },
    
    // Progress permissions
    { value: 'progress:read', label: 'Lihat Progress', category: 'Progress' },
    { value: 'progress:create', label: 'Buat Progress', category: 'Progress' },
    { value: 'progress:update', label: 'Update Progress', category: 'Progress' },
    { value: 'progress:approve', label: 'Approve Progress', category: 'Progress' },
    
    // Document permissions
    { value: 'documents:read', label: 'Lihat Dokumen', category: 'Dokumen' },
    { value: 'documents:upload', label: 'Upload Dokumen', category: 'Dokumen' },
    { value: 'documents:delete', label: 'Hapus Dokumen', category: 'Dokumen' },
    
    // Budget permissions
    { value: 'budget:read', label: 'Lihat Budget', category: 'Budget' },
    { value: 'budget:create', label: 'Buat Budget', category: 'Budget' },
    { value: 'budget:update', label: 'Update Budget', category: 'Budget' },
    { value: 'budget:approve', label: 'Approve Budget', category: 'Budget' },
    
    // User permissions
    { value: 'users:read', label: 'Lihat Pengguna', category: 'Pengguna' },
    { value: 'users:manage', label: 'Kelola Pengguna', category: 'Pengguna' },
    
    // Admin permissions
    { value: 'admin:access', label: 'Akses Admin Panel', category: 'Admin' },
    { value: 'admin:settings', label: 'Kelola Pengaturan', category: 'Admin' },
    { value: 'admin:logs', label: 'Lihat Logs', category: 'Admin' }
  ];

  // Group permissions by category
  const permissionsByCategory = availablePermissions.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = [];
    }
    acc[permission.category].push(permission);
    return acc;
  }, {});

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/roles');
      setRoles(response.data.roles);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setAlert({
        open: true,
        message: 'Gagal memuat data role',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddDialog = () => {
    setFormData({
      name: '',
      description: '',
      permissions: []
    });
    setOpenAddDialog(true);
  };

  const handleOpenEditDialog = (role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: Array.isArray(role.permissions) ? role.permissions : 
                   (typeof role.permissions === 'string' ? JSON.parse(role.permissions) : [])
    });
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (role) => {
    setSelectedRole(role);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenAddDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setSelectedRole(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionChange = (permissionValue) => {
    setFormData(prev => {
      const permissions = [...prev.permissions];
      if (permissions.includes(permissionValue)) {
        return {
          ...prev,
          permissions: permissions.filter(p => p !== permissionValue)
        };
      } else {
        return {
          ...prev,
          permissions: [...permissions, permissionValue]
        };
      }
    });
  };

  const handleSelectAllInCategory = (category, isSelected) => {
    const categoryPermissions = permissionsByCategory[category].map(p => p.value);
    
    setFormData(prev => {
      let updatedPermissions;
      
      if (isSelected) {
        // Add all permissions in this category
        updatedPermissions = [...new Set([...prev.permissions, ...categoryPermissions])];
      } else {
        // Remove all permissions in this category
        updatedPermissions = prev.permissions.filter(p => !categoryPermissions.includes(p));
      }
      
      return {
        ...prev,
        permissions: updatedPermissions
      };
    });
  };

  const isCategoryFullySelected = (category) => {
    const categoryPermissions = permissionsByCategory[category].map(p => p.value);
    return categoryPermissions.every(p => formData.permissions.includes(p));
  };

  const isCategoryPartiallySelected = (category) => {
    const categoryPermissions = permissionsByCategory[category].map(p => p.value);
    return categoryPermissions.some(p => formData.permissions.includes(p)) && 
           !categoryPermissions.every(p => formData.permissions.includes(p));
  };

  const handleAddRole = async () => {
    if (!formData.name) {
      setAlert({
        open: true,
        message: 'Nama role tidak boleh kosong',
        severity: 'error'
      });
      return;
    }
    
    setDialogLoading(true);
    
    try {
      await api.post('/admin/roles', formData);
      
      setAlert({
        open: true,
        message: 'Role berhasil ditambahkan',
        severity: 'success'
      });
      
      handleCloseDialog();
      fetchRoles();
    } catch (error) {
      console.error('Error adding role:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Gagal menambahkan role',
        severity: 'error'
      });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleEditRole = async () => {
    if (!formData.name) {
      setAlert({
        open: true,
        message: 'Nama role tidak boleh kosong',
        severity: 'error'
      });
      return;
    }
    
    setDialogLoading(true);
    
    try {
      await api.put(`/admin/roles/${selectedRole.id}`, formData);
      
      setAlert({
        open: true,
        message: 'Role berhasil diperbarui',
        severity: 'success'
      });
      
      handleCloseDialog();
      fetchRoles();
    } catch (error) {
      console.error('Error updating role:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Gagal memperbarui role',
        severity: 'error'
      });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDeleteRole = async () => {
    setDialogLoading(true);
    
    try {
      await api.delete(`/admin/roles/${selectedRole.id}`);
      
      setAlert({
        open: true,
        message: 'Role berhasil dihapus',
        severity: 'success'
      });
      
      handleCloseDialog();
      fetchRoles();
    } catch (error) {
      console.error('Error deleting role:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Gagal menghapus role',
        severity: 'error'
      });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert(prev => ({ ...prev, open: false }));
  };

  const getPermissionLabel = (permissionValue) => {
    const permission = availablePermissions.find(p => p.value === permissionValue);
    return permission ? permission.label : permissionValue;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Role & Akses
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
        >
          Tambah Role Baru
        </Button>
      </Box>
      
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nama Role</TableCell>
                <TableCell>Deskripsi</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell align="center">Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Tidak ada data role
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((role) => {
                  const rolePermissions = Array.isArray(role.permissions) ? role.permissions : 
                                         (typeof role.permissions === 'string' ? JSON.parse(role.permissions) : []);
                  
                  return (
                    <TableRow key={role.id}>
                      <TableCell>
                        <Typography variant="subtitle2">{role.name}</Typography>
                      </TableCell>
                      <TableCell>{role.description || '-'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {rolePermissions.slice(0, 3).map((permission) => (
                            <Chip
                              key={permission}
                              label={getPermissionLabel(permission)}
                              size="small"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                          {rolePermissions.length > 3 && (
                            <Chip
                              label={`+${rolePermissions.length - 3} lainnya`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit Role">
                          <IconButton size="small" onClick={() => handleOpenEditDialog(role)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Hapus Role">
                          <IconButton size="small" onClick={() => handleOpenDeleteDialog(role)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Add/Edit Role Dialog */}
      <Dialog 
        open={openAddDialog || openEditDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {openAddDialog ? 'Tambah Role Baru' : 'Edit Role'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Nama Role"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Deskripsi"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={2}
            />
            
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
              Permissions
            </Typography>
            
            {Object.entries(permissionsByCategory).map(([category, permissions]) => (
              <Box key={category} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isCategoryFullySelected(category)}
                        indeterminate={isCategoryPartiallySelected(category)}
                        onChange={(e) => handleSelectAllInCategory(category, e.target.checked)}
                      />
                    }
                    label={<Typography variant="subtitle2">{category}</Typography>}
                  />
                </Box>
                <Divider sx={{ my: 1 }} />
                <Grid container spacing={2}>
                  {permissions.map((permission) => (
                    <Grid item xs={12} sm={6} md={4} key={permission.value}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.permissions.includes(permission.value)}
                            onChange={() => handlePermissionChange(permission.value)}
                          />
                        }
                        label={permission.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Batal</Button>
          <Button 
            onClick={openAddDialog ? handleAddRole : handleEditRole} 
            variant="contained" 
            disabled={dialogLoading}
          >
            {dialogLoading ? <CircularProgress size={24} /> : 'Simpan'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Role Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDialog}>
        <DialogTitle>Hapus Role</DialogTitle>
        <DialogContent>
          <Typography>
            Apakah Anda yakin ingin menghapus role <strong>{selectedRole?.name}</strong>? Tindakan ini tidak dapat dibatalkan.
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Perhatian: Menghapus role dapat mempengaruhi pengguna yang saat ini menggunakan role ini.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Batal</Button>
          <Button 
            onClick={handleDeleteRole} 
            color="error"
            disabled={dialogLoading}
          >
            {dialogLoading ? <CircularProgress size={24} /> : 'Hapus'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoleManagement;
